import React from "react";

const LeverageVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson challenges the belief that longer hours equal faster promotion. It explains how hard work works early, then traps you later. The shift is leverage, using people, tools, templates, and delegation to multiply impact without multiplying effort. It shows why leverage wins: bigger visible blast radius, judgment matters more than grinding, and freed time allows next level behaviors like strategy, stakeholder trust, and mentoring.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-6 text-body leading-7 text-[#1A1A1A] overflow-y-scroll font-gotham">
    <p>
      You know what's frustrating? Being the first person in the office and the
      last one to leave... and still watching someone who works half your hours
      get promoted.
    </p>

    <p>
      I've seen this pattern play out dozens of times. Someone's grinding,
      putting in 60-hour weeks, skipping lunch, saying no to everything outside
      work. And when promotion season comes? They get a pat on the back. Maybe
      "keep up the good work." But no promotion letter.
    </p>

    <p>
      Here's the uncomfortable truth:{" "}
      <strong>
        at lower levels, hard work may get you promoted. But beyond that, hard
        work alone keeps you stuck.
      </strong>
    </p>

    <p>
      Early in your career, there's this beautiful, simple equation that could
      work: more effort equals faster progress. You work late, you deliver more,
      you get recognized, you move up.
    </p>

    <p>
      But eventually that creates a belief system.{" "}
      A dangerous belief system.
    </p>

    <p>
      You start thinking: "If I just work harder, if I just put in more hours,
      that's the secret." So when you don't get promoted, you double down. You
      arrive earlier. You stay later. You sacrifice weekends.
    </p>

    <p>
      Meanwhile, you're watching colleagues, people who seem to leave at 5 PM,
      who have time for hobbies, who aren't visibly grinding, they're getting
      promoted.
    </p>

    <p>What do we do then?</p>

    <p>The difference is leverage.</p>

    <p>
      Naval Ravikant, in the book “The Almanac of Naval Ravikant” has said:{" "}
      <strong>
        Leverage is anything that allows you to multiply your output with the
        same or less input.
      </strong>
    </p>

    <p>
      Think of it like this: Imagine you're trying to move a boulder. You can
      push it with your bare hands, that's pure effort. You'll sweat, you'll
      strain, maybe you'll move it a few inches.
    </p>

    <p>
      Or... you can get a lever and a fulcrum. Suddenly, the same amount of your
      energy moves that boulder ten feet.
    </p>

    <p>At work, leverage looks like:</p>

    <ul className="list-disc pl-6 space-y-0.5">
      <li>
        Having a team of three people working on your project instead of doing
        it solo
      </li>
      <li>Building a tool once that saves everyone 2 hours a week</li>
      <li>Creating a template that turns a 4-hour task into a 30-minute task</li>
      <li>Delegating the execution while you focus on strategy</li>
    </ul>

    <p>
      The people who get promoted aren't working harder. They're working with
      leverage. They've figured out how to turn one hour of their time into ten
      hours of impact.
    </p>

    <p>Alright, so why would this get us promoted?</p>

    <p>Three reasons:</p>

    <p>
      <strong>Leverage magnifies your impact, and makes it visible.</strong>
    </p>

    <p>
      When you're doing everything yourself, your impact is limited to what you
      personally can accomplish. Maybe you launch a great feature. Maybe you
      solve a tough bug. That's good, but it's... contained.
    </p>

    <p>
      When you have leverage, when you're coordinating three people, or when
      your tool is being used by 50 teammates, your decisions touch way more
      people. Your good judgment affects way more outcomes. And here's the key:{" "}
      <strong>
        promotion panels notice big, visible impact.
      </strong>{" "}
      They're not counting your hours. They're looking at the blast radius of
      your work.
    </p>

    <p>
      <strong>Leverage rewards judgment over effort.</strong>
    </p>

    <p>
      When you're amplifying your work through others or systems, the{" "}
      <em>quality of your decisions</em> becomes everything. A good strategic
      call with high leverage can be worth more than a month of grinding.
    </p>

    <p>
      Would you rather have someone who can personally write 100 lines of
      perfect code a day, or someone who can make the architectural decision
      that saves the entire team from three months of technical debt?
    </p>

    <p>
      Promotion committees are looking for people who demonstrate next-level
      judgment. Picking high-leverage problems, the cross-team blockers, the
      revenue moments, the repeatable wins, that signals you're thinking like
      someone at the next level.
    </p>

    <p>
      This is the sneaky one, leverage creates freedom, which creates time for
      next-level behavior.
    </p>

    <p>
      Here's what kills most people's promotion chances: their calendar is
      completely full. They're execution-maxed. They have zero time for the
      things that actually signal readiness for the next level.
    </p>

    <p>Things like:</p>

    <ul className="list-disc pl-6 space-y-0.5">
      <li>Strategic thinking and planning</li>
      <li>
        Building relationships with stakeholders outside your immediate team
      </li>
      <li>Mentoring and developing others</li>
      <li>
        Identifying and solving problems before they're assigned to you
      </li>
    </ul>

    <p>
      When you detach your results from your personal hours, through tools,
      through delegation, through clear processes, you free up calendar space.
      And <em>that's</em> when you can start behaving like someone at the next
      level.
    </p>

    <p>
      Managers don't promote people who are drowning in execution. They promote
      people who've proven they can create space to lead.
    </p>

    <p>So here's something to think about:</p>

    <p>
      <strong>
        Your worth is not measured by how hard you work. It's measured by the
        outcomes you create at scale.
      </strong>
    </p>

    <p>
      The person who leaves at 5 PM has built a system that runs smoothly without
      them. They're more promotable than the person working until midnight to
      keep everything afloat through heroics.
    </p>

    <p>
      This might feel counterintuitive. Maybe even uncomfortable. Especially if
      you've built your identity around being the hardest worker in the room.
    </p>

    <p>
      But if you want to get promoted, you need to shift from being the person
      who <em>does the most work</em> to being the person who{" "}
      <em>creates the most leverage.</em>
    </p>
  </div>
);


  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default LeverageVideo;
